import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react"; // Added useCallback
import CrudService from "services/cruds-service";
import LevelMonitor from "./levelMonitor";
import AssetTracker from "./asset-tracker";
import HolyKelDevice from "./holykel-device";
import TabletDevice from "./tablet-device/index";
import { toast } from "react-toastify"; // Assuming toast is available from context
import Loading from "examples/Loading";
import { InfoPage } from "error";

// Define constants for device type IDs to improve readability and maintainability
const DEVICE_TYPE_HOLYKEL = 9;
const DEVICE_TYPE_LEVEL_MONITOR = 4;
const DEVICE_TYPE_TABLET = 10;

const ViewDevice = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [deviceProfile, setDeviceProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentIdRef = useRef(null)

  // Memoize the fetch function to prevent unnecessary re-creations
  const fetchDeviceProfile = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    currentIdRef.current = id
    try {
      const response = await CrudService.checkDeviceType(id);
      if (response && response.data) {
        setDeviceProfile(response.data);
      } else {
        // Handle cases where the response is valid but doesn't contain the expected data
        console.warn("Device profile not found or unexpected response structure for ID:", id);
        setDeviceProfile(null);
        toast.info("Device profile could not be loaded.");
      }
    } catch (err) {
      console.error("Failed to fetch device profile:", err);
      setError(err); // Store the error object

      // Check for specific error status and provide user feedback
      if (err.status === 400) {
        toast.error("Device not found or access denied. Redirecting...");
        navigate("/user-profile");
      } else {
        toast.error("An unexpected error occurred while fetching device details.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]); // Dependencies for useCallback

  useEffect(() => {
    setDeviceProfile(null);
    setError(null);
    setLoading(true);
    fetchDeviceProfile();
  }, [fetchDeviceProfile]); // Dependency for useEffect

  // Helper function to render the correct device component based on its type
  const renderDeviceComponent = () => {
    if (!deviceProfile) {
      return null; // Should ideally be caught by loading/error states, but good for safety
    }
    if(currentIdRef.current !== id){
      return null
    }
    switch (deviceProfile.device_type_id) {
      case DEVICE_TYPE_HOLYKEL:
        let channel_list = deviceProfile?.channels_list ?? []
        return <HolyKelDevice channels={channel_list} default_channel={channel_list.length > 0 ? channel_list[0].channel_number : 0} />;
      case DEVICE_TYPE_LEVEL_MONITOR:
        return <LevelMonitor />;
      case DEVICE_TYPE_TABLET:
        return <TabletDevice />;
      default:
        // Default to AssetTracker for any other device_type_id
        return <AssetTracker />;
    }
  };

  if (loading) {
    return <><Loading transparent={true} /></>; // Simple loading indicator
  }

  // If an error occurred and it wasn't a 400 (which triggers navigation), display a generic error
  if (error && error.status !== 400) {
    return <InfoPage message={'Error: Failed to load device details. Please try again'} />;
    // return <div> <h1> Error: Failed to load device details. Please try again.</h1></div>;
  }

  // If not loading, no error, but deviceProfile is still null, it means no device was found
  if (!deviceProfile) {
    return <InfoPage message={'No device found for the given ID'} />;
    // return <div>No device found for the given ID.</div>;
  }

  return (
    <>
      {renderDeviceComponent()}
    </>
  );
};

export default ViewDevice;
